import { type JSX, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import { formatDate } from "common/helpers/utils";
import type { CreatedTemplate } from "common/types";

import { TEMPLATE_METRICS } from "../../../constants";
import TemplateTypeIcon from "../TemplateTypeIcon";
import DeleteTemplateContent from "./DeleteTemplateContent";

interface CampaignPreviewProps {
  template: CreatedTemplate;
  children: (goBack: () => void) => JSX.Element;
}

export default function MobilePreview({
  template,
  children,
}: CampaignPreviewProps) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [currentStep, setCurrentStep] = useState<"delete" | "edit" | "preview">(
    "preview",
  );

  const { type, created_at: createdAt, name } = template;

  let content = (
    <>
      <div className="-mx-4 flex items-center gap-x-3 border-b border-b-black-200 p-4">
        <TemplateTypeIcon type={type} size="lg" />

        <div className="flex flex-col gap-y-1">
          <h3 className="break-words text-lg-bold">{name}</h3>
          <span className="text-sm-medium text-black-500">
            Created on {formatDate(createdAt)}
          </span>
        </div>
      </div>

      {/* Metrics */}
      <div className="mb-8 flex flex-col">
        {TEMPLATE_METRICS.map(({ getLabel, icon: Icon, getValue }) => {
          const label = getLabel(type);
          return (
            <div
              key={label}
              className="flex items-center gap-x-2 border-b border-b-black-200 py-4 last:border-b-0"
            >
              <div className="flex size-8 items-center justify-center rounded-xl bg-black-100">
                <Icon className="size-4" />
              </div>
              <span className="flex-1 text-sm-bold">{label}</span>
              <span className="text-button-sm">{getValue(template)}</span>
            </div>
          );
        })}
      </div>
      <RestrictedComponent visibleForPermissions={["templates_full"]}>
        <Button
          onClick={() => setCurrentStep("edit")}
          size="lg"
          className="mb-2"
        >
          Edit
        </Button>
      </RestrictedComponent>
      <Button
        variant="outline-danger"
        onClick={() => setCurrentStep("delete")}
        size="lg"
      >
        Delete Template
      </Button>
    </>
  );

  if (currentStep === "delete") {
    content = (
      <DeleteTemplateContent
        templateId={template.id}
        templateName={template.name}
        onCancel={() => setCurrentStep("preview")}
      />
    );
  }

  return (
    <>
      <Drawer
        open={!!currentStep}
        onOpenChange={(open) => {
          if (!open) {
            setCurrentStep(null);
          }
        }}
        onAfterClose={() => navigate({ pathname: "..", search })}
      >
        <DrawerContent>{content}</DrawerContent>
      </Drawer>
      {currentStep === "edit" && children(() => setCurrentStep("preview"))}
    </>
  );
}
